import { DownOutlined } from '@ant-design/icons';
import { Button, InputBox, Modal } from '@imprivata-cloud/components';
import { Dropdown, Form, type MenuProps } from 'antd';
import React, { type ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PurchasedProduct } from '../../../../api/types';
import Hexagon from '../../../../assets/hexagon.svg?react';
import AIPLogo from '../../../../assets/icons/products/aip_logo.svg?react';
import EAMLogo from '../../../../assets/icons/products/eam_logo.svg?react';
import EAMCLogo from '../../../../assets/icons/products/eamc_logo.svg?react';
import IDThreatIcon from '../../../../assets/icons/products/idThreat_logo.svg?react';
import MAMLogo from '../../../../assets/icons/products/mam_logo.svg?react';
import SettingsIcon from '../../../../assets/icons/settings.svg?react';
import { withRole } from '../../../../common/authorization/Roles';
import { ErrorCode } from '../../../../errorHandler/constants';
import { useErrorContext } from '../../../../errorHandler/context/ErrorContext';
import { AppError } from '../../../../errorHandler/errors';
import { productTags, ROLE_TYPE_ADMIN } from '../../../constants';
import { useUpdateSpDisplayName } from '@imprivata-cloud/core';
import './ProductCard.less';

const getIcon = (productTag: string): ReactElement => {
  switch (productTag) {
    case 'access-intel-platform':
      return <AIPLogo />;
    case 'mobile-access-mgmt':
      return <MAMLogo />;
    case 'enterprise-access-mgmt':
      return <EAMLogo />;
    case 'risk-based-access':
      return <IDThreatIcon />;
    case productTags.SSO:
      return <EAMCLogo />;
    default:
      return <EAMLogo />;
  }
};

const TRANSLATION_ROOT = 'portal.landing';

type Props = { product: PurchasedProduct; tenantId: string };

export const ProductCard = ({ product, tenantId }: Props) => {
  const { t } = useTranslation();
  const { emitError } = useErrorContext();

  const [isSettingsOpen, setIsSettingsOpen] = useState<boolean>(false);

  // TODO: @Josue - Remove this and leverage form and react-query cache
  const [originalSpDisplayName, setOriginalSpDisplayName] = useState(product.spDisplayName);
  const [newSpDisplayName, setNewSpDisplayName] = useState('');

  const [form] = Form.useForm();
  const SettingsButton = withRole({ authorizedRoles: [ROLE_TYPE_ADMIN] })(Button);

  const { mutate } = useUpdateSpDisplayName(tenantId);

  const onProductClick = () => {
    if (
      product.productTag === productTags.PATIENT_ACCESS
      || product.productTag === productTags.SSO
    ) {
      //open PA in the same window as they share the session cookie with portal
      window.location.href = product.startSsoLinks[0].startSsoUrl;
    } else {
      // open product in new tab
      window.open(product.startSsoLinks[0].startSsoUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleFormFinish = (values: { spDisplayName: string }) => {
    mutate(
      { spMetadataId: product.spMetadataId, spDisplayName: values.spDisplayName.trim() },
      {
        onSuccess: () => {
          setOriginalSpDisplayName(values.spDisplayName.trim());
          setIsSettingsOpen(false);
        },
        onError: () => {
          emitError(new AppError({ code: ErrorCode.NOT_FOUND }));
        },
      },
    );
  };

  const handleCancel = () => {
    form.resetFields();
    setNewSpDisplayName('');
    setIsSettingsOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewSpDisplayName(e.target.value);
  };

  const isOkDisabled = newSpDisplayName.trim() === '' || newSpDisplayName === originalSpDisplayName;

  const items: MenuProps['items'] = product.startSsoLinks.map(startSsoLink => {
    return {
      key: startSsoLink.applianceDesignator,
      label: (
        <a target="_blank" rel="noopener noreferrer" href={startSsoLink.startSsoUrl}>
          {startSsoLink.applianceDesignator}
        </a>
      ),
    };
  });

  const shouldHideSettings =
    product.productTag === productTags.PATIENT_ACCESS
    || product.spMetadataId === '';

  return (
    <>
      <div className={'card'}>
        <div className={'productHeaderContainer'}>
          {shouldHideSettings ? null : (
            <SettingsButton
              icon={<SettingsIcon fill={'#fff'} />}
              className={'settingsIcon'}
              onClick={() => setIsSettingsOpen(true)}
              aria-label={t(`${TRANSLATION_ROOT}.product-modal.title`)}
            />
          )}

          <div className={'hexagonContainer'}>
            <Hexagon className={'hexagon'} />
            <div className={'productIconContainer'}>
              {React.cloneElement(getIcon(product.productTag), {
                style: { width: '100%', height: '100%' },
              })}
            </div>
          </div>
        </div>

        <div className={'titleContainer'}>
          <div>{product.displayName}</div>
        </div>
        <div className={'linkContainer'}>
          <span className={`text-ellipsis ${'spDisplayName'}`}>{originalSpDisplayName}</span>
          {product.startSsoLinks.length < 2 ? (
            <Button
              onClick={onProductClick}
              label={t(`${TRANSLATION_ROOT}.launch`)}
              type={'text'}
            />
          ) : (
            <Dropdown menu={{ items }} trigger={['click']}>
              <Button type={'text'}>
                Launch <DownOutlined />
              </Button>
            </Dropdown>
          )}
        </div>
      </div>
      <Modal
        open={isSettingsOpen}
        title={t(`${TRANSLATION_ROOT}.product-modal.title`)}
        width={400}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        onOk={() => form.submit()}
        okButtonProps={{ disabled: isOkDisabled }}
        onCancel={handleCancel}
        // w/o this no autoFocus on modal reopen
        destroyOnHidden
        maskClosable={false}
      >
        <Form
          form={form}
          initialValues={{ spDisplayName: originalSpDisplayName }}
          onFinish={handleFormFinish}
          layout="vertical"
          style={{ paddingRight: '0.5rem', margin: '2rem 0' }}
        >
          <Form.Item
            label={t(`${TRANSLATION_ROOT}.product-modal.descriptive-label`)}
            name={'spDisplayName'}
          >
            <InputBox
              autoFocus
              autofocusDelayMs={300}
              onChange={handleInputChange}
              maxLength={20}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
